import { User as FirebaseUser } from 'firebase/auth';
import { auth } from 'firebase-config';

export interface AppUser {
  uid: string;
  email: string;
  role: 'admin' | 'user';
  displayName: string;
  organizationId?: string | null; 
  active: boolean;
  createdAt: any; 
  lastLogin: any; 
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const getAuthHeader = async (forceRefresh: boolean = false) => {
    const user = auth.currentUser;
    if (!user) {
        throw new Error("User not authenticated");
    }
    const token = await user.getIdToken(forceRefresh);
    return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
    };
};

export const fetchUserFromBackend = async (firebaseUser: FirebaseUser): Promise<AppUser> => {
  const token = await firebaseUser.getIdToken(true); 
  const response = await fetch(`${API_BASE_URL}/api/v1/users/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch user data from backend');
  }

  const data = await response.json();
  return data.user; 
};

export const getAllUsers = async (): Promise<AppUser[]> => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/v1/users`, {
        headers,
    });

    if (!response.ok) {
        throw new Error('Failed to fetch users');
    }

    return response.json();
};

export const updateUserOrganization = async (uid: string, organizationId: string | null): Promise<void> => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/v1/users/${uid}/organization`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ organizationId }),
    });

    if (!response.ok) {
        throw new Error('Failed to update user organization');
    }
};

export const updateUserActiveStatus = async (uid: string, active: boolean): Promise<void> => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/v1/users/${uid}/active`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ active }),
    });

    if (!response.ok) {
        throw new Error('Failed to update user active status');
    }
};

export const promoteToAdmin = async (uid: string): Promise<void> => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/v1/users/${uid}/promote`, {
        method: 'PUT',
        headers,
    });

    if (!response.ok) {
        throw new Error('Failed to promote user to admin');
    }
};

export const demoteToUser = async (uid: string): Promise<void> => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/v1/users/${uid}/demote`, {
        method: 'PUT',
        headers,
    });

    if (!response.ok) {
        throw new Error('Failed to demote user');
    }
};

export const updateUserProfile = async (uid: string, displayName: string): Promise<void> => {
    const headers = await getAuthHeader();
    const response = await fetch(`${API_BASE_URL}/api/v1/users/${uid}/profile`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ displayName }),
    });

    if (!response.ok) {
        throw new Error('Failed to update user profile');
    }
};